import { useCallback, useEffect, useRef, useState } from 'react';

import { VaultRiskBreachPage, listVaultRiskBreaches } from 'src/pages/admin/services/vaultService';

import { RISK_DEFAULT_ROWS_PER_PAGE, RISK_OPEN_PAGE_LIMIT } from './constants';
import { riskBadge } from './helpers';

/**
 * A vault's risk breaches (XBIT-4764, C-06 vaultRiskBreaches).
 *
 * ONE small read feeds the header badge and the default table: the open episodes, a page of the five limits (at most
 * one is open per vault and limit, so it holds them all). "Show all" is a separate paged read (`open: false` = every
 * episode, open and closed) that happens only while the toggle is on, so a page load costs one read. A response
 * that arrives after the vault, the toggle or the page changed is dropped (the `latest` counters).
 *
 * A failed read is `failed`, never an empty page: the badge says "unavailable" and the table says so, so an
 * unreadable monitor is not read as a clean vault.
 */
export const useVaultRiskBreaches = (vaultId: string) => {
  const latestOpen = useRef(0);
  const latestAll = useRef(0);
  const [open, setOpen] = useState<VaultRiskBreachPage | null>(null);
  const [openFailed, setOpenFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(RISK_DEFAULT_ROWS_PER_PAGE);
  const [all, setAll] = useState<VaultRiskBreachPage | null>(null);
  const [allFailed, setAllFailed] = useState(false);
  const [allLoading, setAllLoading] = useState(false);

  const readOpen = useCallback(async () => {
    latestOpen.current += 1;
    const call = latestOpen.current;
    try {
      const page = await listVaultRiskBreaches(vaultId, {
        open: true,
        offset: 0,
        limit: RISK_OPEN_PAGE_LIMIT,
      });
      if (call !== latestOpen.current) return;
      setOpen(page);
      setOpenFailed(false);
    } catch {
      if (call !== latestOpen.current) return;
      setOpen(null);
      setOpenFailed(true);
    }
  }, [vaultId]);

  const readAll = useCallback(async () => {
    latestAll.current += 1;
    const call = latestAll.current;
    setAllLoading(true);
    try {
      const page = await listVaultRiskBreaches(vaultId, {
        open: false,
        offset: pageIndex * rowsPerPage,
        limit: rowsPerPage,
      });
      if (call !== latestAll.current) return;
      setAll(page);
      setAllFailed(false);
    } catch {
      if (call !== latestAll.current) return;
      setAll(null);
      setAllFailed(true);
    } finally {
      if (call === latestAll.current) setAllLoading(false);
    }
  }, [vaultId, pageIndex, rowsPerPage]);

  // A new vault starts clean: the previous vault's episodes must not linger under the new name.
  useEffect(() => {
    setOpen(null);
    setOpenFailed(false);
    setAll(null);
    setAllFailed(false);
    setShowAll(false);
    setPageIndex(0);
    readOpen();
  }, [vaultId, readOpen]);

  useEffect(() => {
    if (showAll) readAll();
  }, [showAll, readAll]);

  const toggleShowAll = useCallback((next: boolean) => {
    setShowAll(next);
    setPageIndex(0);
  }, []);

  const refetch = useCallback(async () => {
    await readOpen();
    if (showAll) await readAll();
  }, [readOpen, readAll, showAll]);

  return {
    badge: riskBadge(open, openFailed),
    showAll,
    toggleShowAll,
    /** The table's page: every episode while "Show all" is on, else the open ones the badge read. */
    page: showAll ? all : open,
    failed: showAll ? allFailed : openFailed,
    loading: showAll && allLoading,
    pageIndex,
    setPageIndex,
    rowsPerPage,
    setRowsPerPage: (n: number) => {
      setRowsPerPage(n);
      setPageIndex(0);
    },
    refetch,
  };
};

export type VaultRiskBreachesState = ReturnType<typeof useVaultRiskBreaches>;
